import React, { Suspense, lazy, useEffect, useState } from 'react';
import { Routes, Route, useNavigate, Navigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { jwtDecode } from 'jwt-decode';
import { Spin } from 'antd';

const GetStartedScreen = lazy(() => import('../screens/GetStarted'));
const Layout = lazy(() => import('../screens/Layout'));
const Dashboard = lazy(() => import('../screens/Dashboard'));
const NewApp = lazy(() => import('../screens/NewApp'));
const ManageApplications = lazy(() => import('../screens/ManageApplications'));
const ManageDetails = lazy(() => import('../screens/ApplicationDetails'));
const Requests = lazy(() => import('../screens/Requests'));
const Notification = lazy(() => import('../screens/Notification'));

const ProtectedRoute = ({ isAuthenticated, children }) => {
  return isAuthenticated ? children : <Navigate to="/" replace />;
};

// const getTokenExpiryInfo = (token) => {
//   try {
//     const { exp } = jwtDecode(token);
//     const currentTime = Date.now() / 1000; // in seconds
//     const timeLeft = exp - currentTime;
//     const hours = Math.floor(timeLeft / 3600);
//     const minutes = Math.floor((timeLeft % 3600) / 60);
//     const seconds = Math.floor(timeLeft % 60);
//     console.log(`Token expires in ${hours}h ${minutes}m ${seconds}s`);
//     return {
//       isExpired: timeLeft <= 0,
//       isExpiringSoon: timeLeft <= 5, // expires in next 5 seconds
//       timeLeft: Math.max(timeLeft, 0), // just in case
//     };
//   } catch (e) {
//     console.error("Error decoding token:", e);
//     return { isExpired: true, isExpiringSoon: false, timeLeft: 0 };
//   }
// };

const AppRoutes = () => {
  const navigate = useNavigate();
  const { instance } = useMsal();
  const { openMessage, setUserTheme } = useTheme();
  const { setUser, setAccessToken } = useAuth();
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    const token = localStorage.getItem("authToken") || new URLSearchParams(window.location.search).get("token");
    return token ? true : false;
  });
  let token = new URLSearchParams(window.location.search).get("token") || localStorage.getItem("authToken");

  const getUserFromToken = (token) => {
    if (token) {
      try {
        const decodedToken = jwtDecode(token);
        const userName = decodedToken.name || "Unknown User";
        const userMail = decodedToken.unique_name || "Unknown User";
        localStorage.setItem("authToken", token); // Save token in localStorage
        setUser({ name: userName, email: userMail });
        setUserTheme({ name: userName, email: userMail });
        setAccessToken(token);
        setIsAuthenticated(true);
        navigate("/layout");
        return;
      } catch (error) {
        console.error("Invalid token:", error);
        localStorage.removeItem("authToken"); // Remove invalid token
      }
    }

    // ❌ Only set false if no valid token is found
    setIsAuthenticated(false);
  };

  useEffect(() => {
    getUserFromToken(token);
  }, [token]);

  // useEffect(() => {
  //   if (!token) return;

  //   const intervalId = setInterval(() => {
  //     getTokenExpiryInfo(token); // This already logs the expiry info
  //     const { isExpired, isExpiringSoon } = getTokenExpiryInfo(token);
  //     console.log(`Token is expired: ${isExpired}, Expiring soon: ${isExpiringSoon}`);
  //   }, 1000); // every 1 second

  //   return () => clearInterval(intervalId); // cleanup on unmount or token change
  // }, [token]);

  // 🔥 Optimized handleLogin function
  const handleLogin = async () => {
    try {
      await instance.loginPopup();
      const currentAccounts = instance.getAllAccounts();

      if (currentAccounts.length > 0) {
        const request = {
          account: currentAccounts[0],
          scopes: ['User.Read'],
        };

        const response = await instance.acquireTokenSilent(request);
        const accessToken = response.accessToken;
        setAccessToken(response.accessToken)
        localStorage.setItem("authToken", accessToken);
        // Fetch profile picture with fallback handling
        const fetchProfilePicture = async () => {
          try {
            const graphResponse = await fetch('https://graph.microsoft.com/v1.0/me/photo/$value', {
              headers: { Authorization: `Bearer ${accessToken}` },
            });

            if (graphResponse.ok) {
              const blob = await graphResponse.blob();
              const url = URL.createObjectURL(blob);
              return url;
            }

            if (graphResponse.status === 404) {
              console.warn('Profile picture not found');
              return null;
            }

            console.error('Failed to fetch profile picture', graphResponse.status);
            return null;

          } catch (error) {
            console.error('Error fetching profile picture:', error);
            return null;
          }
        };

        const profilePicture = await fetchProfilePicture();
        setIsAuthenticated(true)
        // Update user state dynamically
        setUserTheme({
          name: currentAccounts[0].name,
          email: currentAccounts[0].username,
          profilePicture: profilePicture || null
        });
        setUser({
          name: currentAccounts[0].name,
          email: currentAccounts[0].username,
          profilePicture: profilePicture || null
        });

        navigate("/layout");

      } else {
        console.warn("No accounts found after login.");
        openMessage('error', 'No account found. Please try again.');
      }

    } catch (error) {
      console.error('Login failed:', error);
      setIsAuthenticated(false)
      openMessage('error', 'Login failed. Please try again.');
    }
  };

  return (
    <>
      <Suspense fallback={<div style={styles.loadingContainer}><Spin size="large" style={{ marginBottom: 10 }} /> Loading AFS Platform...</div>}>
        <Routes>
          <Route
            path="/"
            element={<GetStartedScreen handleLogin={handleLogin} />}
          />
          <Route path="/layout" element={<ProtectedRoute isAuthenticated={isAuthenticated}><Layout /> </ProtectedRoute>}>
            <Route index element={<Dashboard />} />
            <Route path="NewApp" element={<NewApp />} />
            <Route path="manageapplications" element={<ManageApplications />} />
            <Route path="applicationsDetails" element={<ManageDetails />} />
            <Route path="requests" element={<Requests />} />
            <Route path="notifications" element={<Notification />} />
          </Route>
        </Routes>

      </Suspense>
    </>
  );
};

const styles = {
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    height: '100vh',
  },
};

export default AppRoutes;
